import React, { createContext, ReactNode, useState, useEffect } from "react";
import { appPreferences } from "../utils";
import { IMenu } from "../components/common/SideBarMenu/sideBarMenuTypes";
import sideBarMenuService from "../components/common/SideBarMenu/sideBarMenuService";

interface AuthContextProps {
  isAuthenticated: boolean;
  // True while the persisted auth state is still being restored on load.
  // Routing decisions must wait for this to be false, otherwise a page reload
  // (e.g. triggered by the Bhashini language widget) transiently reads as
  // "logged out" and bounces the user off their current route to /dashboard.
  isAuthLoading: boolean;
  sideMenuOpen: boolean;
  setSideMenuOpen: (open: boolean) => void;
  userDetails: any;
  userToken: string;
  accessDetails: IMenu[];
  login: () => void;
  logout: () => void;
  setUserDetailsToContext: (userDetails: "") => void;
  setUserTokenToContext: (userToken: string) => void;
  setAccessDetailsToContext: (accessDetails: []) => void;
  refreshAccessDetails: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextProps | undefined>(
  undefined,
);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isAuthLoading, setIsAuthLoading] = useState<boolean>(true);
  const [sideMenuOpen, setSideMenuOpen] = useState<boolean>(false);
  const [userDetails, setUserDetails] = useState<any>(null);
  const [userToken, setUserToken] = useState<any>(null);
  const [accessDetails, setAccessDetails] = useState<any>(null);

  const login = async () => {
    // Discard any profile/access data left over from a previous account on
    // this browser (e.g. someone else's session, or an earlier test login).
    // Header only re-fetches userDetails when it is empty, so without this a
    // fresh login would keep showing the PREVIOUS account's role/name until
    // the user explicitly logged out.
    setUserDetails(null);
    setAccessDetails(null);
    await appPreferences.removeItem("userDetails");
    await appPreferences.removeItem("accessDetails");
    setIsAuthenticated(true);
  };

  const logout = async () => {
    setIsAuthenticated(false);
    setUserDetails(null);
    setUserToken(null);
    setAccessDetails(null);
    await appPreferences.clearItems();
  };

  const setUserDetailsToContext = async (userDetails: any) => {
    setUserDetails(userDetails);
    await appPreferences.setItem("userDetails", JSON.stringify(userDetails));
  };

  const setUserTokenToContext = async (userToken: string) => {
    setUserToken(userToken);
    await appPreferences.setItem("userToken", userToken);
  };

  const setAccessDetailsToContext = async (accessDetails: any) => {
    setAccessDetails(accessDetails);
    await appPreferences.setItem(
      "accessDetails",
      JSON.stringify(accessDetails),
    );
  };

  // Re-fetches the sidebar menu/permissions from the server and overwrites
  // the cached copy. accessDetails is otherwise only fetched once per login
  // and persisted to storage, so without this, permission changes (e.g.
  // editing your own role) stay invisible until you log out and back in.
  const refreshAccessDetails = async () => {
    try {
      const response = await sideBarMenuService.getCombinedAccess();
      await setAccessDetailsToContext(response.data.data);
    } catch (error) {
      // Keep the previously cached access details on failure.
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const storedToken = await appPreferences.getItem("userToken");
        const storedUserDetails = await appPreferences.getItem("userDetails");
        const storedAccessDetails =
          await appPreferences.getItem("accessDetails");

        setIsAuthenticated(() => {
          return storedToken ? true : false;
        });
        setUserToken(storedToken);
        setUserDetails(() => {
          return storedUserDetails ? JSON.parse(storedUserDetails) : null;
        });
        setAccessDetails(() => {
          return storedAccessDetails ? JSON.parse(storedAccessDetails) : null;
        });
      } finally {
        // Auth state is now resolved — allow routing decisions to proceed.
        setIsAuthLoading(false);
      }
    })();
  }, []);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        isAuthLoading,
        userDetails,
        userToken,
        accessDetails,
        login,
        logout,
        setUserDetailsToContext,
        setUserTokenToContext,
        setAccessDetailsToContext,
        refreshAccessDetails,
        sideMenuOpen,
        setSideMenuOpen,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
